import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowRight, Check, AlertTriangle, Loader2 } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function BookingSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");

  const [phase, setPhase] = useState("polling"); // polling | paid | failed | timeout
  const [data, setData] = useState(null);
  const attempts = useRef(0);
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setPhase("failed");
      return;
    }

    const confirmThenPoll = async () => {
      // Tell the backend Stripe redirected us back — primary confirmation signal.
      try {
        const { data: confirmed } = await api.post(`/checkout/confirm/${sessionId}`);
        setData(confirmed);
        if (confirmed.payment_status === "paid") {
          stopped.current = true;
          setPhase("paid");
          return;
        }
      } catch (e) {
        console.warn("checkout confirm failed", formatApiError(e));
      }

      // Fallback: poll status (covers webhook-only delivery in production)
      const poll = async () => {
        if (stopped.current) return;
        attempts.current += 1;
        try {
          const { data: status } = await api.get(`/checkout/status/${sessionId}`);
          setData(status);
          if (status.payment_status === "paid") {
            stopped.current = true;
            setPhase("paid");
            return;
          }
          if (status.status === "expired" || status.payment_status === "failed") {
            stopped.current = true;
            setPhase("failed");
            return;
          }
          if (attempts.current >= MAX_ATTEMPTS) {
            stopped.current = true;
            setPhase("timeout");
            return;
          }
          setTimeout(poll, POLL_INTERVAL_MS);
        } catch (e) {
          console.error("checkout status poll failed", formatApiError(e));
          if (attempts.current >= MAX_ATTEMPTS) {
            stopped.current = true;
            setPhase("timeout");
            return;
          }
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      };
      poll();
    };

    confirmThenPoll();
    return () => {
      stopped.current = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-cream">
      <Header />

      <section className="mx-auto max-w-3xl px-6 py-24 md:py-32 text-center" data-testid="booking-success-page">
        {phase === "polling" && <Polling />}
        {phase === "paid" && <Paid data={data} />}
        {phase === "failed" && <Failed />}
        {phase === "timeout" && <Timeout sessionId={sessionId} />}
      </section>

      <Footer />
    </div>
  );
}

function Polling() {
  return (
    <div data-testid="booking-success-polling">
      <div className="mx-auto h-16 w-16 border border-gold rounded-full flex items-center justify-center">
        <Loader2 className="h-7 w-7 text-gold animate-spin" strokeWidth={1.5} />
      </div>
      <div className="eyebrow mt-8">Confirming Payment</div>
      <h1 className="mt-4 font-serif text-5xl md:text-6xl text-forest leading-[0.95]">
        Just a moment…
      </h1>
      <p className="mt-6 text-forest-muted font-light">
        We're verifying your payment with Stripe. This usually takes a few seconds.
      </p>
    </div>
  );
}

function Paid({ data }) {
  const dollars = data ? (data.amount_total / 100).toFixed(2) : "—";
  return (
    <div data-testid="booking-success-paid">
      <div className="mx-auto h-16 w-16 border border-gold rounded-full flex items-center justify-center">
        <Check className="h-8 w-8 text-gold" strokeWidth={1.5} />
      </div>
      <div className="eyebrow mt-8">Paid &amp; Confirmed</div>
      <h1 className="mt-4 font-serif text-5xl md:text-6xl text-forest leading-[0.95]">
        See you on the range.
      </h1>
      <div className="mt-10 border border-cream-border bg-cream-light p-10 text-left">
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="eyebrow">Booking</div>
            <div className="mt-2 font-serif text-xl text-forest break-all">{data?.booking_id}</div>
          </div>
          <div>
            <div className="eyebrow">Status</div>
            <div className="mt-2 font-serif text-xl text-forest capitalize">
              {data?.booking_status || "confirmed"}
            </div>
          </div>
          <div>
            <div className="eyebrow">Amount paid</div>
            <div className="mt-2 font-serif text-2xl text-gold">
              ${dollars} <span className="text-xs uppercase tracking-widest text-forest-muted ml-1">{data?.currency}</span>
            </div>
          </div>
          <div>
            <div className="eyebrow">Receipt</div>
            <div className="mt-2 text-xs text-forest-muted">Emailed by Stripe</div>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-cream-border text-sm text-forest-muted leading-relaxed">
          <strong className="text-forest">All set.</strong> Your lesson is paid and confirmed. Free cancellation up
          to 24 hours before — refunds processed within 5 business days.
        </div>
      </div>
      <div className="mt-10 flex flex-wrap gap-4 justify-center">
        <Link to="/dashboard" data-testid="success-go-dashboard" className="btn-primary">
          View my lessons <ArrowRight className="ml-3 h-4 w-4" />
        </Link>
        <Link to="/coaches" className="btn-secondary">
          Browse more coaches
        </Link>
      </div>
    </div>
  );
}

function Failed() {
  return (
    <div data-testid="booking-success-failed">
      <div className="mx-auto h-16 w-16 border border-destructive rounded-full flex items-center justify-center">
        <AlertTriangle className="h-7 w-7 text-destructive" strokeWidth={1.5} />
      </div>
      <div className="eyebrow mt-8 text-destructive">Payment didn't go through</div>
      <h1 className="mt-4 font-serif text-5xl md:text-6xl text-forest leading-[0.95]">
        Let's try that again.
      </h1>
      <p className="mt-6 text-forest-muted font-light">
        Your card wasn't charged. Pick a coach and try the booking again — sometimes Stripe just needs a fresh start.
      </p>
      <div className="mt-10 flex flex-wrap gap-4 justify-center">
        <Link to="/coaches" className="btn-primary">
          Back to coaches
        </Link>
      </div>
    </div>
  );
}

function Timeout({ sessionId }) {
  return (
    <div data-testid="booking-success-timeout">
      <div className="mx-auto h-16 w-16 border border-cream-border rounded-full flex items-center justify-center">
        <Loader2 className="h-7 w-7 text-forest-muted" strokeWidth={1.5} />
      </div>
      <div className="eyebrow mt-8">Still verifying</div>
      <h1 className="mt-4 font-serif text-5xl md:text-6xl text-forest leading-[0.95]">
        Almost there.
      </h1>
      <p className="mt-6 text-forest-muted font-light max-w-xl mx-auto">
        Payment is taking a little longer than usual. Your booking will appear in your dashboard within a minute —
        you'll receive a confirmation email from Stripe as soon as it's settled.
      </p>
      <div className="mt-10 flex flex-wrap gap-4 justify-center">
        <Link to="/dashboard" className="btn-primary">
          Check my lessons <ArrowRight className="ml-3 h-4 w-4" />
        </Link>
      </div>
      {sessionId && (
        <p className="mt-6 text-[10px] tracking-widest uppercase text-forest-muted">
          ref: {sessionId}
        </p>
      )}
    </div>
  );
}
